import React from 'react';
import AboveTheFoldOnlyServerRender from '../../shared/components/above-the-fold-only-server-render';
import useTemplateTheme from '../../hooks/useTemplateTheme';
import usePageComponents from '../../hooks/usePageComponents';
import { useClientSideABTest } from '../../abtest/components/abtest';

import PDPOverview from './pdp-overview';
import Features from './pdp-overview/exclusive-features';
import RichContent from './pdp-overview/pdp-rich-content';
import PDPSpecs from './pdp-specs';
import PDPOffers from './pdp-offers';
import Reviews from './reviews';
import PDPCrossSell from './cross-sell';
import PDPUpSell from './upsell';
import CompatibleProducts from './compatible-products';
import FAQS from './faqs';
import UGC, { ugcContainerID } from './ugc';

const DEFAULT_LAZY_LOAD_SETTINGS = {
    skip: true,
    robots: true,
};

/**
 * Ensures all sections from DEFAULT_SECTION_ORDER are included
 * Appends any missing sections to the end in their default order
 * Always pins pdpOverview as the first section
 */
const ensureCompleteSectionOrder = order => {
    if (!order || order.length === 0) {
        return DEFAULT_SECTION_ORDER;
    }

    // Remove pdpOverview from the order (will be pinned at the start)
    const orderWithoutOverview = order.filter(section => section !== 'pdpOverview');

    // Find missing sections (excluding pdpOverview since it's pinned)
    const missingSections = DEFAULT_SECTION_ORDER.filter(
        section => section !== 'pdpOverview' && !orderWithoutOverview.includes(section),
    );

    // Always pin pdpOverview first, then apply the custom order, then append missing sections
    return ['pdpOverview', ...orderWithoutOverview, ...missingSections];
};

/**
 * Custom hook to determine PDP tab/section order
 * Priority: AB test > theme tabOrder > pdpSettings.sectionOrder > DEFAULT_SECTION_ORDER
 * Note: pdpOverview is always pinned first and all default sections are included
 * @returns {Object} - { tabOrder: Array, navBar: Object } - Complete ordered array of hash IDs and navBar theme
 */
export const usePdpTabOrder = () => {
    const { navBar } = useTemplateTheme(['navBar']);
    const { pdpSettings } = usePageComponents(['pdpSettings']);
    const { tabOrder: themeTabOrder } = navBar || {};
    const { sectionOrder } = pdpSettings || {};
    const { sectionOrder: abTestOrder } = useClientSideABTest('pdpSectionOrder') || {};

    // Priority: AB test > theme tabOrder > pdpSettings.sectionOrder > null
    const rawTabOrder =
        abTestOrder && abTestOrder.length > 0
            ? abTestOrder
            : themeTabOrder && themeTabOrder.length > 0
              ? themeTabOrder
              : sectionOrder && sectionOrder.length > 0
                ? sectionOrder
                : null;

    // Ensure complete section order with pdpOverview pinned first
    const tabOrder = ensureCompleteSectionOrder(rawTabOrder);

    return { tabOrder, navBar };
};

/**
 * Section component mapping keyed by sticky-nav hash IDs
 * Aligns with hash IDs used in sticky-nav getAnchorLinks()
 */
const SECTION_COMPONENTS = {
    overviewDetails: () => <PDPOverview />,
    compatibleProducts: () => (
        <AboveTheFoldOnlyServerRender skip={true} robots={true}>
            <CompatibleProducts />
        </AboveTheFoldOnlyServerRender>
    ),
    recommendedAccessories: () => <PDPCrossSell />,
    features: () => (
        <section id="features">
            <RichContent />
            <Features />
        </section>
    ),
    techSpecs: () => <PDPSpecs />,
    specialOffers: () => <PDPOffers />,
    faq: () => <FAQS />,
    [ugcContainerID]: () => <UGC />,
    reviews: () => <Reviews />,
    similarProducts: () => <PDPUpSell />,
};

/**
 * Default section order - fallback if navBar.tabOrder is not defined
 */
const DEFAULT_SECTION_ORDER = [
    'pdpOverview',
    'overviewDetails',
    'compatibleProducts',
    'recommendedAccessories',
    'features',
    'techSpecs',
    'specialOffers',
    'faq',
    ugcContainerID,
    'reviews',
    'questions',
    'similarProducts',
];

/**
 * Memoized section wrapper - prevents re-renders when order changes
 */
const Section = React.memo(({ hash }) => {
    const Component = SECTION_COMPONENTS[hash];
    return Component ? <Component /> : null;
});

Section.displayName = 'PDPSection';

/**
 * Dynamic PDP layout component
 * Section order is determined by usePdpTabOrder hook
 * pdpOverview is always pinned as the first section
 * All default sections are guaranteed to be included
 */
const PDPLayout = () => {
    const { tabOrder } = usePdpTabOrder();

    return (
        <>
            {tabOrder.map(hash => (
                <Section key={hash} hash={hash} />
            ))}
        </>
    );
};

export default PDPLayout;

// Export the mapping and constants for reuse
export { SECTION_COMPONENTS, DEFAULT_SECTION_ORDER };

/**
 * Conditionally render specs based on theme position (backward compatibility)
 */
export const SpecsByTemplate = ({ position }) => {
    const { techSpecs } = useTemplateTheme(['techSpecs']);
    const { position: themePosition = 'default' } = techSpecs || {};

    if (themePosition === position) {
        return <PDPSpecs lazyLoadSettings={DEFAULT_LAZY_LOAD_SETTINGS} />;
    }
};
